import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { db, greensDb } from "@/lib/firebase";
import type { PlayerProfile } from "@/lib/types";

export type GreenLayout = {
  imageUrl?: string;
  slopeUrl?: string;
  gridImagePath?: string;
  slopeImagePath?: string;
  // Pixel-exact crop of the slope+arrow panel, cropped to the same green
  // boundary bbox the vectorizer normalizes vector x/y against. Used as the
  // ball/pin tap target when present, since imageUrl (a full-page photo) is
  // not in the same coordinate frame as the stored vectors.
  vectorTapImageUrl?: string;
  // Per-hole calibration for converting real-world GPS/front-distance offsets
  // into vectorTapImageUrl-frame coordinates. The tap-crop is a straight,
  // unrotated crop of however that hole's page was printed -- NOT rotated to
  // align with holeAzimuthDeg (the tee->green approach bearing) -- and each
  // green's real size varies, so holeAzimuthDeg + an assumed 90x90ft box is
  // the wrong frame for this specific conversion (confirmed on Brentwood
  // hole 16, which plotted a pin sheet entry clearly off the green under the
  // old assumption). imageUpTrueBearingDeg is the true compass bearing that
  // the tap-crop's "up" direction represents, measured from the printed
  // compass rose's red (North) needle. When present, use these in place of
  // holeAzimuthDeg/45ft defaults for that conversion specifically -- NOT for
  // other holeAzimuthDeg uses like wind classification, which still want the
  // real approach bearing.
  imageUpTrueBearingDeg?: number;
  greenHalfWidthFt?: number;
  greenHalfDepthFt?: number;
  // Half-dimensions (in the SAME real feet as greenHalfWidthFt/greenHalfDepthFt)
  // that vectorTapImageUrl actually depicts. The tap-crop is rendered with
  // extra margin around the green itself so the fringe/apron where real
  // approach shots land and settle is visible, not just the putting surface
  // -- a tight-to-boundary crop was reported as "severely cropped" even on
  // holes whose calibration was never touched. The margin is capped per hole
  // per axis to whatever real page content is actually available around the
  // green (no synthetic padding, to avoid streaked/smeared edge artifacts),
  // so it is NOT a single fixed ratio across holes -- always divide these by
  // greenHalfWidthFt/greenHalfDepthFt to get the actual per-axis ratio
  // rather than assuming a constant. Falls back to 1:1 (no margin) when
  // absent, matching the original tight crop behavior.
  tapImageHalfWidthFt?: number;
  tapImageHalfDepthFt?: number;
  // Per-hole shape profile replacing the flat greenHalfWidthFt for distance
  // ("24F 6R") pin-sheet entry on greens whose true cross-section varies
  // enough that a single half-width is meaningfully wrong at some pin
  // depths -- see WidthProfilePoint in holeTargetPresets.ts. Only present
  // for holes with EZLocator ground-truth to trace it from; falls back to
  // the flat greenHalfWidthFt model when absent.
  widthProfile?: Array<{ frac: number; halfWidthFt: number; centerOffsetFt: number }>;
  displayName?: string;
  holeNumber?: number;
  holeAzimuthDeg?: number;
  updatedAt?: any;
};

function isOfflineFirestoreError(err: unknown): boolean {
  const code = (err as any)?.code;
  const msg = String((err as any)?.message ?? "").toLowerCase();
  return (
    code === "unavailable" ||
    msg.includes("client is offline") ||
    msg.includes("offline")
  );
}

// -----------------------------
// Player Profiles
// Collection: playerProfiles/{playerId}
// -----------------------------
export async function loadPlayerProfile(playerId: string) {
  const id = (playerId ?? "").trim();
  if (!id) return null;

  const ref = doc(db, "playerProfiles", id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;

  return snap.data();
}

export async function savePlayerProfile(
  playerId: string,
  profile: PlayerProfile
): Promise<boolean> {
  try {
    const id = (playerId ?? "").trim();
    if (!id) return false;

    const ref = doc(db, "playerProfiles", id);
    await setDoc(ref, { ...profile, id, updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (err: any) {
    console.warn("[DAL] savePlayerProfile failed", err?.code, err?.message, err);
    if (isOfflineFirestoreError(err)) return false;
    throw err;
  }
}

// -----------------------------
// Green Layouts (in greenlayouts DB)
// Path: courses/{courseId}/holes/{hole}
// -----------------------------
export async function loadGreenLayout(
  courseId: string,
  hole: number
): Promise<GreenLayout | null> {
  try {
    const cid = (courseId ?? "").trim();
    const hid = String(hole);

    if (!cid) {
      return null;
    }

    const ref = doc(greensDb, "courses", cid, "holes", hid);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;

    const data = snap.data() as GreenLayout;
    return data;
  } catch (err: any) {
    console.warn("[DAL] loadGreenLayout failed", err?.code, err?.message);
    return null;
  }
}

// -----------------------------
// Shot logs
// Collection: shotLogs (auto-id)
// -----------------------------
// -----------------------------
// Green Vectors
// Path: courses/{courseId}/holes/{hole} — same doc as GreenLayout, vectors field
// -----------------------------
export async function loadGreenVectors(
  courseId: string,
  hole: number
): Promise<import("@/lib/greenVectors").GreenVector[] | null> {
  try {
    const cid = (courseId ?? "").trim();
    const hid = String(hole);
    if (!cid) return null;

    const ref = doc(greensDb, "courses", cid, "holes", hid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;

    const data = snap.data();
    const vectors = data?.vectors;
    if (!Array.isArray(vectors) || vectors.length === 0) return null;

    return vectors as import("@/lib/greenVectors").GreenVector[];
  } catch (err: any) {
    console.warn("[DAL] loadGreenVectors failed", err?.code, err?.message);
    return null;
  }
}

export async function logShot(data: Record<string, unknown>): Promise<boolean> {
  try {
    await addDoc(collection(db, "shotLogs"), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (err: any) {
    console.warn("[DAL] logShot failed", err?.code, err?.message, err);
    if (isOfflineFirestoreError(err)) return false;
    throw err;
  }
}
export async function getRecentShotsForPlayer(
  playerId: string,
  n = 200
): Promise<any[]> {
  const pid = (playerId ?? "").trim();
  if (!pid) return [];

  const q = query(
    collection(db, "shotLogs"),
    where("playerId", "==", pid),
    orderBy("createdAt", "desc"),
    limit(n)
  );

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
export async function getShotsForSession(
  playerId: string,
  sessionId: string
): Promise<any[]> {
  const pid = (playerId ?? "").trim();
  const sid = (sessionId ?? "").trim();
  if (!pid || !sid) return [];

  const q = query(
    collection(db, "shotLogs"),
    where("playerId", "==", pid),
    where("sessionId", "==", sid),
    orderBy("shotSeq", "asc")
  );

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
export function tallyClubFrequency(shots: any[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const s of shots) {
    const club = String(s.club ?? "unknown");
    counts[club] = (counts[club] ?? 0) + 1;
  }
  return counts;
}

export async function getRecentShotsForPlayerCourse(
  playerId: string,
  courseId: string,
  n = 200
): Promise<any[]> {
  const pid = (playerId ?? "").trim();
  const cid = (courseId ?? "").trim();
  if (!pid || !cid) return [];

  const q = query(
    collection(db, "shotLogs"),
    where("playerId", "==", pid),
    where("courseId", "==", cid),
    orderBy("createdAt", "desc"),
    limit(n)
  );

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
export function computePlaysLikeDeltaStats(shots: any[]) {
  const deltas: number[] = [];
  for (const s of shots) {
    const raw = Number(s.rawYardage);
    const ply = Number(s.playsLikeYards);
    if (!Number.isFinite(raw) || !Number.isFinite(ply)) continue;
    deltas.push(ply - raw);
  }
  return deltas;
}

export function tallyWindBins(shots: any[]) {
  const bins = Array(8).fill(0);

  for (const s of shots) {
    const bin = Number(s.windDirBin);
    if (!Number.isInteger(bin) || bin < 0 || bin > 7) continue;
    bins[bin]++;
  }

  return bins;
}
export async function getShotsForCourseHole(
  courseId: string,
  hole: number,
  n = 500
): Promise<any[]> {
  const cid = (courseId ?? "").trim();
  const h = Number(hole);

  if (!cid || !Number.isFinite(h)) return [];

  const q = query(
    collection(db, "shotLogs"),
    where("courseId", "==", cid),
    where("hole", "==", h),
    orderBy("createdAt", "desc"),
    limit(n)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export function summarizeGlobalWindForHole(shots: any[]) {
  const bins = Array.from({ length: 8 }, (_, i) => ({
    bin: i,
    directionRange: `${i * 45}–${i * 45 + 44}`,
    count: 0,
    avgWindSpeedMph: 0,
  }));

  let totalCount = 0;
  let totalWindSpeed = 0;

  for (const s of shots) {
    const bin = Number(s.windDirBin);
    const mph = Number(s.windSpeedMph);

    if (!Number.isFinite(bin) || bin < 0 || bin > 7) continue;
    if (!Number.isFinite(mph)) continue;

    bins[bin].count += 1;
    bins[bin].avgWindSpeedMph += mph;

    totalCount += 1;
    totalWindSpeed += mph;
  }

  for (const b of bins) {
    b.avgWindSpeedMph = b.count ? b.avgWindSpeedMph / b.count : 0;
  }

  return {
    totalCount,
    avgWindSpeedMph: totalCount ? totalWindSpeed / totalCount : 0,
    bins,
  };
}

// -----------------------------
// Pin Sheet (per-round pin positions loaded before play)
// Path: pinSheets/{courseId}/holes/{hole} — in the greenlayouts DB, same
// pattern as GreenLayout. Overwritten each time the pre-round setup screen
// is run, so it always reflects "today's" pin sheet for that course.
// -----------------------------
export type PinSheetEntry = {
  xNorm: number;
  yNorm: number;
  lat: number;
  lng: number;
  updatedAt?: any;
};

export async function savePinSheetHole(
  courseId: string,
  hole: number,
  entry: { xNorm: number; yNorm: number; lat: number; lng: number }
): Promise<boolean> {
  try {
    const cid = (courseId ?? "").trim();
    if (!cid) return false;

    const ref = doc(greensDb, "pinSheets", cid, "holes", String(hole));
    await setDoc(ref, { ...entry, updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (err: any) {
    console.warn("[DAL] savePinSheetHole failed", err?.code, err?.message);
    if (isOfflineFirestoreError(err)) return false;
    throw err;
  }
}

export async function loadPinSheet(
  courseId: string
): Promise<Record<number, PinSheetEntry>> {
  try {
    const cid = (courseId ?? "").trim();
    if (!cid) return {};

    const snap = await getDocs(collection(greensDb, "pinSheets", cid, "holes"));
    const result: Record<number, PinSheetEntry> = {};
    snap.forEach((d) => {
      const hole = Number(d.id);
      if (Number.isFinite(hole)) {
        result[hole] = d.data() as PinSheetEntry;
      }
    });
    return result;
  } catch (err: any) {
    console.warn("[DAL] loadPinSheet failed", err?.code, err?.message);
    return {};
  }
}

export async function clearAllPinSheetHoles(
  courseId: string,
  holes: number[]
): Promise<boolean> {
  const results = await Promise.all(holes.map((h) => clearPinSheetHole(courseId, h)));
  return results.every(Boolean);
}

export async function clearPinSheetHole(courseId: string, hole: number): Promise<boolean> {
  try {
    const cid = (courseId ?? "").trim();
    if (!cid) return false;

    const ref = doc(greensDb, "pinSheets", cid, "holes", String(hole));
    await setDoc(ref, { xNorm: null, yNorm: null, lat: null, lng: null, updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (err: any) {
    console.warn("[DAL] clearPinSheetHole failed", err?.code, err?.message);
    return false;
  }
}